import { Ops } from '@willie/schemas'
import type { WillieSupabaseClient } from '@willie/supabase'
import { OPS_BUCKET } from '@willie/supabase/storage'
import { toUploadableImage } from '~/features/installations/heic'

const { attachmentPath, classifyFile } = Ops.InstallationAttachments

export type MeterPhotoRejection = {
  filename: string
  reason: Ops.InstallationAttachments.PhotoRejectionReason | 'upload-failed'
}

/**
 * Stores one meter photo straight from the browser, as the other ops photo uploads do: superusers
 * may write the ops bucket and installation_attachments directly. HEIC is converted to JPEG first.
 */
const uploadOne = async (
  supabaseClient: WillieSupabaseClient,
  { installationId, file, uploadedBy }: { installationId: number; file: File; uploadedBy: string | null }
): Promise<MeterPhotoRejection | null> => {
  const photo = await toUploadableImage(file)
  const verdict = classifyFile({ contentType: photo.type, size: photo.size })
  if ('reason' in verdict) return { filename: file.name, reason: verdict.reason }

  const storagePath = attachmentPath(installationId, 'meter_photo', crypto.randomUUID(), verdict.contentType)
  const upload = await supabaseClient.storage
    .from(OPS_BUCKET)
    .upload(storagePath, photo, { contentType: verdict.contentType })
  if (upload.error) return { filename: file.name, reason: 'upload-failed' }

  const { error } = await supabaseClient.from('installation_attachments').insert({
    installation_id: installationId,
    kind: 'meter_photo',
    storage_path: storagePath,
    created_by_email: uploadedBy
  })
  if (!error) return null
  await supabaseClient.storage.from(OPS_BUCKET).remove([storagePath])
  return { filename: file.name, reason: 'upload-failed' }
}

/** Returns the files that did not become meter photos, with why. */
export const uploadMeterPhotos = async (
  supabaseClient: WillieSupabaseClient,
  params: { installationId: number; files: File[]; uploadedBy: string | null }
): Promise<MeterPhotoRejection[]> => {
  const outcomes = await Promise.all(params.files.map((file) => uploadOne(supabaseClient, { ...params, file })))
  return outcomes.filter((outcome) => outcome !== null)
}

// The row goes first, so nothing is left pointing at a missing object.
export const deleteMeterPhoto = async (
  supabaseClient: WillieSupabaseClient,
  photo: { id: string; storage_path: string }
): Promise<void> => {
  const { error } = await supabaseClient.from('installation_attachments').delete().eq('id', photo.id)
  if (error) throw error
  await supabaseClient.storage.from(OPS_BUCKET).remove([photo.storage_path])
}
